'use client'

/**
 * The banner's hidden-threads control (Issue #13653).
 *
 * A count badge for the feedback threads on this route that the pin overlay
 * could not draw — their element is inside a collapsed section, a closed tab
 * panel, anything not laid out — and, on activation, a panel listing them so
 * each can be opened directly. Why this shape and not a bare badge is
 * recorded in `pin-overlay-mount.tsx`, which publishes the data.
 *
 * ## Nothing new is invented for the thread itself
 *
 * The panel is `FeedbackPinPopover`, the exact component a pin opens, given
 * the hidden pins. Its thread switcher is the list; a single hidden thread
 * opens straight into its timeline and reply box. A second thread viewer
 * here would be a second place for reply and triage behaviour to drift.
 *
 * ## The shell copies `feedback-pin.tsx`'s, for the same reasons
 *
 *  - `z-feedback-popover` — above the picker's `z-feedback-picker` capture
 *    layer and the banner's `z-feedback-banner`, below `FeedbackModal`'s
 *    `z-feedback-modal`, and (#16841) above every ordinary modal's `z-modal`
 *    and every `z-menu` (sai-ui's layer scale since #16990). Important
 *    (`!`) to beat the package content's own `z-modal`, as there.
 *  - `data-feedback-mode-exempt`, so text typed into the reply box is never
 *    the element the picker resolves under the cursor.
 *  - `setPinThreadOpen` publishes open/close and is withdrawn on
 *    unmount, so the picker stands down while the panel is up and is never
 *    latched off when the control disappears (the count reaching zero, or
 *    the mode ending, unmounts it with no Radix close event).
 *
 * sai-ui `Popover` since #15028, not `@radix-ui/react-popover` directly, on
 * the same terms as `feedback-pin.tsx`: the package paints the panel, so the
 * `Card` is gone, and the dialog is named "Hidden feedback threads".
 */

import { useEffect, useRef, useState } from 'react'
import { Popover, PopoverContent, PopoverTrigger } from '@sai/ui/components'
import { EyeOff } from '@/components/icons'
import { cn } from '@/lib/utils'
import { trackFeedbackModeEvent } from '@/app/actions/feedback-mode-analytics'
import { FeedbackPinPopover } from '@/components/feedback/feedback-pin-popover'
import { FEEDBACK_MODE_EXEMPT_ATTRIBUTE } from './feedback-mode-attributes'
import { useFeedbackMode } from './feedback-mode-context'

function hiddenLabel(count: number): string {
  const threads = count === 1 ? 'thread' : 'threads'
  return `${count} hidden feedback ${threads} on this page: show them`
}

interface HiddenFeedbackThreadsProps {
  /** The banner's control class, so this reads as one of its controls. */
  className: string
  /** The banner's narrow-viewport label rule. */
  labelClassName: string
}

/**
 * Renders the control only while there is something to show. The stateful
 * part lives in {@link HiddenThreadsControl} so that the count reaching zero
 * UNMOUNTS it — an early `return null` here would keep its `open` state and
 * skip its cleanup, leaving the picker suspended and the panel re-opening by
 * itself on the next route that has hidden threads (the overlay clears its
 * pins before every refetch, so zero is a routine transition, not an edge).
 */
export function HiddenFeedbackThreads(props: HiddenFeedbackThreadsProps) {
  const { hiddenThreads, hiddenPinCount } = useFeedbackMode()
  if (hiddenThreads === null || hiddenPinCount === 0) return null
  return <HiddenThreadsControl {...props} />
}

function HiddenThreadsControl({
  className,
  labelClassName,
}: HiddenFeedbackThreadsProps) {
  const {
    hiddenThreads,
    hiddenPinCount,
    setPinThreadOpen,
    requestAnchorRefresh,
  } = useFeedbackMode()
  const [open, setOpen] = useState(false)

  const openRef = useRef(false)
  openRef.current = open
  useEffect(() => {
    return () => {
      if (openRef.current) setPinThreadOpen(false)
    }
  }, [setPinThreadOpen])

  // The parent unmounts this before either can happen; narrowed for the type.
  if (hiddenThreads === null) return null

  return (
    <Popover
      open={open}
      onOpenChange={next => {
        setOpen(next)
        setPinThreadOpen(next)
        if (next) {
          // The count and nothing else — never a feedback id, which names
          // one person's submission.
          void trackFeedbackModeEvent('feedback.hidden_threads_opened', {
            hiddenCount: hiddenPinCount,
          }).catch(() => {})
        }
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="feedback-hidden-threads-toggle"
          aria-label={hiddenLabel(hiddenPinCount)}
          className={className}
        >
          <EyeOff className="w-4 h-4" aria-hidden="true" />
          <span className={labelClassName}>Hidden</span>
          {/*
            The badge inverts the bar's own pair — the bar's foreground as a
            fill, the bar's fill as text — so it stays legible in both modes
            without a colour of its own. `aria-hidden` because the count is
            already in the control's accessible name.
          */}
          <span
            data-testid="feedback-hidden-threads-count"
            aria-hidden="true"
            className={cn(
              'inline-flex min-w-5 h-5 items-center justify-center px-1.5',
              'rounded-full text-xs font-semibold tabular-nums',
              'bg-feedback-mode-foreground text-feedback-mode-banner',
            )}
          >
            {hiddenPinCount}
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="end"
        sideOffset={8}
        collisionPadding={16}
        aria-label="Hidden feedback threads"
        data-testid="feedback-hidden-threads-list"
        {...{ [FEEDBACK_MODE_EXEMPT_ATTRIBUTE]: 'true' }}
        className="z-feedback-popover! w-auto overflow-hidden p-4"
      >
        <p className="mb-3 text-xs text-text-secondary">
          {hiddenPinCount === 1
            ? 'This thread is on a part of the page that is collapsed, ' +
              'hidden, or no longer shown.'
            : 'These threads are on parts of the page that are ' +
              'collapsed, hidden, or no longer shown.'}
        </p>
        <FeedbackPinPopover
          pins={hiddenThreads.pins}
          isAdmin={hiddenThreads.isAdmin}
          timezone={hiddenThreads.timezone}
          onTriaged={requestAnchorRefresh}
        />
      </PopoverContent>
    </Popover>
  )
}
